import { NavigationItemType } from '@lumapps/navigation/types';
import { SearchResultWithTitleAndUrl, SearchSuggestionType, SelectedFacets } from '@lumapps/search/types';

export enum TrackingEventType {
    ASK_CLICK = 'ask_click',
    ASK_COPY = 'ask_copy',
    ASK_EXPAND = 'ask_expand',
    CLICK_ACTION = 'click_action',
    NOTIFICATION_CLICK = 'notification_click',
    NAVIGATION_CLICK = 'navigation_click',
    SEARCH = 'search',
    SEARCH_CLICK = 'search_click',
    SEARCH_CLICK_SUGGESTION = 'search_click_suggestion',
    SEARCH_CLICK_PREVIEW = 'search_click_preview',
    SEARCH_FILTER = 'search_filter',
    SEARCH_LOAD_MORE = 'search_load_more',
    SEARCH_IN_SPACE = 'search_in_space',
    SEARCH_CLICK_IN_SPACE = 'search_click_in_space',
    SEARCH_CLICK_PREVIEW_IN_SPACE = 'search_click_preview_in_space',
    SEARCH_FILTER_IN_SPACE = 'search_filter_in_space',
    SEARCH_LEAVE = 'search_leave',
    SEARCH_LOAD_MORE_IN_SPACE = 'search_load_more_in_space',
    SEARCH_REACTION_IN_SPACE = 'search_reaction_in_space',
    SHARE = 'share',
    VIEW = 'view',
}

export enum TrackingAppType {
    IOS = 'ios',
    ANDROID = 'android',
    RESPONSIVE = 'responsive',
    DESKTOP = 'desktop',
    EXTERNAL = 'external',
}

export enum TrackingReferrerType {
    EMAIL = 'email',
    NOTIFICATION = 'notification',
    LINK = 'link',
    MOBILE_APP = 'mobile-app',
}

export enum TargetType {
    ARTICLE = 'article',
    ASK_REACTION = 'ask_reaction',
    COMMENT = 'comment',
    COMMUNITY = 'community',
    CONTENT = 'content',
    DIRECTORY_ENTRY = 'directory_entry',
    EVENT = 'event',
    EXTENSION = 'extension',
    EXTERNAL_LINK = 'external_link',
    GROUP = 'group',
    LEARNING_PATH = 'learning_path',
    MEDIA = 'media',
    MOBILE = 'mobile',
    NAVIGATION_ITEM = 'navigation_item',
    ORGANIZATION = 'organization',
    PLAY_VIDEO = 'play_video',
    PLAYLIST = 'playlist',
    POST = 'post',
    PROGRAM = 'program',
    SEARCH = 'search',
    SEARCH_EXTENSION = 'search_extension',
    SEARCH_REACTION = 'search_reaction',
    SHAREABLE_CONTENT = 'shareable_content',
    SITE = 'site',
    RECOMMENDATION_QUIZ = 'recommendation_quiz',
    SCORM_TRAINING = 'scorm_training',
    TRAINING_COURSE = 'training_course',
    USER = 'user',
}

export enum SearchDocumentType {
    PLAY_VIDEO = 'video',
    PLAY_PLAYLIST = 'video-playlist',
    USER = 'user',
    CONTENT = 'content',
    POST = 'post',
    COMMUNITY = 'community',
    ARTICLE = 'entity.article',
    EVENT = 'event',
    DIRECTORY_ENTRY = 'directoryentry',
    MEDIA = 'media',
    EXTENSION = 'extension',
    GCS_ARTICLE = 'articles',
    GCS_DIRECTORY_ENTRY = 'directory_entries',
    GCS_POST = 'posts',
    GCS_COMMUNITY = 'communities',
    GCS_USER = 'users',
}

/**
 * List of entity types that can be shared.
 * As for now, shared entity types are the same that we can react (REACTION_RESOURCE_TYPE).
 * Make sure it matches.
 */
export enum SharedEntityType {
    ARTICLE = 'article',
    COMMENT = 'comment',
    CONTENT = 'content',
    EVENT = 'event',
    PLAY_VIDEO = 'play-video',
    POST = 'post',
}

/** /!\ this enum is a copy of LINK_PREVIEW_RESOURCE_TYPES from wrex/link-preview package. Make sure it's ALWAYS 100% the same */
export enum NotificationTargetType {
    ARTICLE = 'article',
    COMMUNITY = 'community',
    CONTENT = 'content',
    EVENT = 'event',
    LEARNING_PATH = 'learning_path',
    MICRO_APP = 'micro_app',
    PLAY_VIDEO = 'play_video',
    PLAY_VIDEO_PLAYLIST = 'play_video_playlist',
    POST = 'post',
    SCORM_TRAINING = 'scorm_training',
    RECOMMENDATION_QUIZ = 'recommendation_quiz',
    TRAINING_COURSE = 'training_course',
}

/** /!\ this enum is a copy of CHANNEL_TYPES from journeys/core package. Make sure it's ALWAYS 100% the same */
export enum NotificationChannelType {
    EMAIL = 'email',
    MARKETPLACE_EXTENSION = 'marketplace_extension',
    MOBILE_PUSH = 'mobile_push',
}

export type LumappsTrackingEventAction = {
    traceId: string;
    type: TrackingEventType;
    query: string;
    resultCount: number;
    searchEngine?: string | null;
    extensionId?: string;
    sortedBy?: string;
    tabId?: string;
    promotedIds?: string[];
    filters?: SelectedFacets;
    position?: number;
    isPromoted?: boolean;
    targetType?: string;
    targetId?: string;
    title?: string;
    url?: string;
    thumbnail?: string;
    displayedResults?: SearchResultWithTitleAndUrl[];
};

export type LumappsTrackingSearchClickEventAction = {
    type: TrackingEventType;
    query: string;
    tabId?: string;
    resultCount: number;
    isPromoted: boolean;
    position: number;
    targetId: string;
    targetType: SearchDocumentType;
    traceId: string;
};

export type LumappsTrackingSearchLeaveAction = {
    type: TrackingEventType.SEARCH_LEAVE;
    /** Unique ID identifying the search this event relates to */
    traceId: string;
    /** Search query the user was on when leaving the search results page */
    query: string;
    /** Selected search tab when leaving */
    tabId?: string;
    /** Search engine that answered the query (provided by the backend) */
    searchEngine?: string | null;
    /** Time the search results page was visible since the last search_leave event */
    timeSpentMs: number;
    /** Time the Ask AI answer was both visible and in the viewport since the last search_leave event */
    timeSpentWithAskAIMs: number;
    /** Word count of the displayed Ask AI answer; 0 when no answer is displayed */
    askAIAnswerLengthWords: number;
    /** Deepest scroll ratio (0 to 1) reached on the page since the last search_leave event */
    maxScrollDepth: number;
};

/**
 * The surfaces distinguished by tracking events (snake_case).
 *
 * A surface identifies where in the product the tracked item was rendered, so that the same
 * entity viewed or clicked from different places can be told apart.
 *
 * Not restricted to feeds: today's values happen to be feeds, but any surface worth
 * distinguishing (a search result list, a widget, a page section) belongs here.
 *
 * Mirrors the closed `surface` enum of the tracking spec (core `Surface` StrEnum): the backend
 * rejects any other value with a 400, and `sendTracking` swallows 400s, so an unknown surface
 * silently drops the whole event batch. A new surface must therefore ship in core first.
 */
export type TrackingSurface = 'personal_feed' | 'personal_feed_preview' | 'space_feed';

export type LumappsTrackingClickAction = {
    type: TrackingEventType.CLICK_ACTION;
    targetId: string;
    targetType: TargetType;
    url?: string;
    thumbnail?: string;
    title?: string;
    /** The surface the tracked item was rendered in, when applicable. */
    surface?: TrackingSurface;
};

/** A single suggestion displayed in the search box, as tracked in a search_click_suggestion event */
export type SearchClickSuggestionDisplayedResult = {
    /** Entity id of the suggestion, when it points to a resource */
    targetId?: string;
    /** Entity type of the suggestion, when it points to a resource */
    targetType?: TargetType;
    /** Url of the suggestion, when it points to a resource */
    url?: string;
    /** Displayed label of the suggestion */
    title: string;
    /** Type of the suggestion (e.g. interaction, suggestion, history, recommendation) */
    suggestionType: SearchSuggestionType;
};

export type LumappsTrackingSearchClickSuggestionAction = {
    type: TrackingEventType.SEARCH_CLICK_SUGGESTION;
    /** Search query; empty string when no query has been typed yet */
    query: string;
    /** Entity id of the clicked suggestion */
    targetId: string;
    /** Entity type of the clicked suggestion */
    targetType: TargetType;
    /** Url of the clicked suggestion, when it points to a resource */
    url?: string;
    /** Displayed label of the clicked suggestion */
    title: string;
    /** Type of the clicked suggestion (interaction or suggestion) */
    suggestionType: SearchSuggestionType.SUGGESTION | SearchSuggestionType.INTERACTION;
    /** Position of the clicked suggestion among all displayed suggestions (1-based) */
    position: number;
    /** All the suggestions displayed to the user when the click occurred */
    displayedResults: SearchClickSuggestionDisplayedResult[];
};

export type LumappsTrackingNotificationClickAction = {
    type: TrackingEventType.NOTIFICATION_CLICK;
    targetId: string;
    targetType: NotificationTargetType;
    url?: string;
    title?: string;
    notificationId: string;
    channelType: NotificationChannelType;
};
// This type will include all tracking actions possible from WREX link preview
export type LumappsTrackingLinkPreviewAction = LumappsTrackingNotificationClickAction;

export type LumappsTrackingImpression = {
    targetId: string;
    targetType: TargetType;
    title?: string;
};

export type LumappsTrackingViewAction = {
    type: TrackingEventType.VIEW;
    targetId: string;
    targetType: TargetType;
    url?: string;
    title?: string;
    /** The surface the tracked item was rendered in, when applicable. */
    surface?: TrackingSurface;
};

export type LumappsTrackingShareAction = {
    type: TrackingEventType.SHARE;
    extensionId?: string;
    sharedResourceId: string;
    sharedResourceType: SharedEntityType;
};

export type LumappsTrackingAskClickAction = {
    type: TrackingEventType.ASK_CLICK;
    traceId: string;
    query: string;
    targetId: string;
    targetType: string;
    url: string;
    title: string;
};

export type LumappsTrackingCopyAction = {
    type: TrackingEventType.ASK_COPY;
    traceId: string;
    query: string;
};

export type LumappsTrackingExpandAction = {
    type: TrackingEventType.ASK_EXPAND;
    traceId: string;
    query: string;
};

/**
 * Navigation
 */

export interface NavigationItemProperties {
    depth: number;
    id: string;
    position: number;
    siblingCount: number;
    title: string;
    type: NavigationItemType;
}

export interface LumappsTrackingNavigationNodeClickAction {
    navigationItemProperties: NavigationItemProperties;
    targetId: string;
    targetType: TargetType.NAVIGATION_ITEM;
    type: TrackingEventType.NAVIGATION_CLICK;
}

/**
 * General tracking
 */

export interface LumappsTrackingEventContext {
    siteId?: string;
    app: {
        type?: TrackingAppType;
        branding?: string;
        version: string;
        build?: string;
    };
    locale?: string;
    page?: {
        path?: string;
        referrer?: string;
        search?: string;
        title?: string;
        url?: string;
    };
    referrer?: {
        type?: TrackingReferrerType;
        name?: string;
        url?: string;
        link?: string;
    };
    screen?: {
        width?: string;
        height?: string;
        density?: string;
    };
    timezone?: string;
}

export type LumappsTrackingActionType =
    | LumappsTrackingClickAction
    | LumappsTrackingEventAction
    | LumappsTrackingNavigationNodeClickAction
    | LumappsTrackingShareAction
    | LumappsTrackingViewAction
    | LumappsTrackingLinkPreviewAction
    | LumappsTrackingSearchClickSuggestionAction
    | LumappsTrackingSearchLeaveAction
    | LumappsTrackingAskClickAction
    | LumappsTrackingCopyAction
    | LumappsTrackingExpandAction;

export interface LumappsTrackingEvent {
    action: LumappsTrackingActionType;
    sessionId: string;
    performedAt: string;
    context: LumappsTrackingEventContext;
}

export interface Config {
    apiUrl: string;
}

export interface LumappsPageViewEventPayload {
    id?: string;
    status?: string;
}

export interface LumappsTrackEventPayload {
    action: LumappsTrackingActionType;
}

export interface SessionId {
    id: string;
    ttl: string;
}
